import { useEffect, useState, useMemo } from "react";
import queryString from "query-string";
import { Link } from "react-router-dom";
import sweetAlert from "sweetalert";

import { Breadcrumbs, Loading, Page, Pagination, Panel } from "components";
import vouchersAPI from "services/vouchers";
import { Access } from "common";

import { Filters } from "./components";

const breadcrumbs = [
  { name: "Home", to: "/" },
  { name: "Pontos", to: "/points" },
  { name: "Vouchers", to: "" },
];

const initialState = {
  value: [],
  discount: [],
  title: [],
};

export function List(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [vouchers, setVouchers] = useState([]);
  const [totalVouchers, setTotalVouchers] = useState(0);
  const [filters, setFilters] = useState(initialState);
  const [inputValue, setInputValue] = useState("");
  const [inputDiscount, setInputDiscount] = useState("");
  const [inputTitle, setInputTitle] = useState("");

  const pagination = useMemo(() => {
    const params = queryString.parse(props.location.search);
    const page = (params.page && Number(params.page)) || 1;
    const per = (params.per && Number(params.per)) || 25;

    return { page, per };
  }, [props.location.search]);

  function handleFiltersChange(name, value) {
    setFilters((current) => ({
      ...current,
      [name]: Array.isArray(value) ? value : [...current[name], value],
    }));
  }

  function handleFiltersClean(event) {
    event.preventDefault();
    setFilters(initialState);
  }

  useEffect(() => {
    function fetchData() {
      setIsLoading(true);

      const { page, per } = pagination;

      const options = {
        params: {
          per,
          page,
          value: filters.value.map(({ value }) => value),
          discount: filters.discount.map(({ value }) => value),
          title: filters.title.map(({ value }) => value),
        },
      };

      vouchersAPI
        .get("experience/vouchers/money", options)
        .then(({ data }) => {
          if (!data || !data.data) {
            return;
          }

          if (Array.isArray(data.data)) {
            setVouchers(
              data.data.map(({ id, attributes }) => ({
                id,
                title: attributes.title,
                alias: attributes.alias,
                value: attributes.value,
                discount: attributes.discount,
                days_to_expire: attributes.days_to_expire,
                invalidable: attributes.invalidable,
                active: attributes.active,
              }))
            );
          }

          if (data.meta && data.meta.total_count) {
            setTotalVouchers(data.meta.total_count);
          }
        })
        .catch((error) => {
          sweetAlert(
            "Algo deu errado!",
            "Não foi possível buscar os dados. Por favor, recarregue a página ou tente fazer o login novamente.",
            "error"
          );
          console.error(error);
        })
        .finally(() => setIsLoading(false));
    }

    fetchData();
  }, [pagination, filters]);

  return (
    <Page>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <Page.Title>Lista de Vouchers (Pontos)</Page.Title>

      <Filters
        filters={filters}
        onChange={handleFiltersChange}
        onClean={handleFiltersClean}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        inputDiscount={inputDiscount}
        onInputDiscountChange={setInputDiscount}
        inputTitle={inputTitle}
        onInputTitleChange={setInputTitle}
      />

      <Panel className="panel-list">
        <div className="vouchers-options">
          <p>
            Exibindo {vouchers.length} de {totalVouchers} vouchers
          </p>
          {Access.hasPermissions("edit-vouchers") && (
            <Link
              className="button-add margin-bottom-20"
              to="/points/vouchers/create"
            >
              Adicionar Voucher
            </Link>
          )}
        </div>

        <>
          <table className="table table-curved">
            <thead>
              <tr>
                <th>Título</th>
                <th>Alias</th>
                <th>Pontos</th>
                <th>Valor</th>
                <th>Validade</th>
                <th>Invalidável</th>
                <th>Ativo</th>
                <th className="text-center">Ação</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <Loading type="page" size="large" />
              ) : !vouchers.length ? (
                <tr>
                  <td className="text-center" colSpan="10">
                    Nenhum voucher encontrado.
                  </td>
                </tr>
              ) : (
                vouchers.map((voucher) => (
                  <tr key={voucher.id}>
                    <td>{voucher.title}</td>
                    <td>{voucher.alias}</td>
                    <td>{voucher.value}</td>
                    <td>{`R$ ${voucher.discount}`}</td>
                    <td>{voucher.days_to_expire}</td>
                    <td
                      className={
                        voucher.invalidable ? "label-active" : "label-inactive"
                      }
                    >
                      {voucher.invalidable ? "Sim" : "Não"}
                    </td>
                    <td
                      className={
                        voucher.active ? "label-active" : "label-inactive"
                      }
                    >
                      {voucher.active ? "Sim" : "Não"}
                    </td>
                    <td className="text-right">
                      {Access.hasPermissions("edit-vouchers") && (
                        <Link
                          to={`/points/vouchers/${voucher.id}/edit`}
                          className="button-icon"
                        >
                          <span className="glyph glyph-pencil" />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot>
              <tr>
                <th colSpan="7" />
              </tr>
            </tfoot>
          </table>

          <Pagination
            current={pagination.page}
            per={pagination.per}
            total={totalVouchers}
            {...props}
          />

          {Access.hasPermissions("edit-vouchers") && (
            <Link className="button-add" to="/points/vouchers/create">
              Adicionar Voucher
            </Link>
          )}
        </>
      </Panel>
    </Page>
  );
}
